import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Checkbox,
  Stack,
} from '@chakra-ui/core';
import React, { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';
import { useStoreState } from '../../../../models/hooks';

interface LanguageReviewProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentLanguage: string;
  defaultLanguage: string;
  availableLanguages: string[];
}

const arePropsEqual = (prevProps: LanguageReviewProps, nextProps: LanguageReviewProps) =>
  // eslint-disable-next-line
  prevProps.isOpen === nextProps.isOpen &&
  prevProps.onClose === nextProps.onClose &&
  prevProps.onConfirm === nextProps.onConfirm &&
  prevProps.currentLanguage === nextProps.currentLanguage &&
  prevProps.defaultLanguage === nextProps.defaultLanguage &&
  isEqual(prevProps.availableLanguages, nextProps.availableLanguages);

const LanguageReview: FC<LanguageReviewProps> = React.memo(function LanguageReviewModalComponent({
  isOpen,
  onClose,
  onConfirm,
  currentLanguage,
  defaultLanguage,
  availableLanguages,
}: LanguageReviewProps) {
  const { t } = useTranslation('hrFormTemplate');
  const hasErrors = useStoreState((state) => {
    const errors = state.formBuilder.validationErrors;
    return errors && errors.length > 0;
  });

  const [isConfirmChecked, setIsConfirmChecked] = useState(false);

  const isMultiLanguageAndNotDefault = availableLanguages.length > 1 && currentLanguage !== defaultLanguage;

  const modalMessage = useMemo(() => {
    if (isMultiLanguageAndNotDefault) {
      return t('formBuilder.languageReviewModal.multiLanguageMessage', {
        currentLanguageName: currentLanguage,
        defaultLanguageName: defaultLanguage,
        defaultValue:
          'You are currently editing in a language ({{currentLanguageName}}) that is not the primary language ({{defaultLanguageName}}). Ensure all translations are reviewed before saving.',
      });
    }
    return t('formBuilder.languageReviewModal.message');
  }, [t, currentLanguage, defaultLanguage, isMultiLanguageAndNotDefault]);

  useEffect(() => {
    if (!isOpen) {
      setIsConfirmChecked(false);
    }
  }, [isOpen]);

  const handleCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsConfirmChecked(e.target.checked);
  }, []);

  const handleConfirmClick = useCallback(() => {
    if (hasErrors) {
      onClose();
    } else {
      onConfirm();
    }
  }, [hasErrors, onConfirm, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      isCentered
      autoFocus={false}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      blockScrollOnMount
    >
      <ModalOverlay />
      <ModalContent data-testid="languageReviewModal" borderRadius="md">
        <ModalHeader borderBottomWidth="1px" fontSize="lg" fontWeight="semibold">
          {t('formBuilder.languageReviewModal.title')}
        </ModalHeader>

        <ModalBody py={6}>
          <Stack spacing={5}>
            <Text>{modalMessage}</Text>
            <Checkbox
              onChange={handleCheckboxChange}
              isChecked={isConfirmChecked}
              data-testid="language-review-modal-checkbox"
              colorScheme="blue"
              size="md"
            >
              <Text fontSize="sm">{t('formBuilder.languageReviewModal.confirmText')}</Text>
            </Checkbox>
          </Stack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" pt={3} pb={3}>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('formBuilder.cancel')}
          </Button>
          <Button
            colorScheme="green"
            onClick={handleConfirmClick}
            isDisabled={!isConfirmChecked}
            data-testid="language-review-modal-confirm-button"
          >
            {t('formBuilder.languageReviewModal.confirmButton')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
},
arePropsEqual);

export default LanguageReview;
